import Decimal from 'decimal.js'

import { resolveBandBounds } from '@/features/advanced-twap/lib/advanced-twap-band'
import { AdvancedTwapState } from '@/types/generated/algo'

export function formatPercent(fraction: string): string {
  try {
    return `${new Decimal(fraction || '0').times(100).toFixed(2)}%`
  } catch {
    return fraction
  }
}

// currentSliceNumber is 0-based on the wire; render 1-based. totalSlices here must
// always be the state's own resolved value, never the requested count from the form —
// the 10s-per-slice floor can silently reduce it (contract §5).
export function sliceLabel(currentSliceNumber: number, totalSlices: number): string {
  return `Slice ${currentSliceNumber + 1} of ${totalSlices}`
}

export interface ResolvedSlicing {
  totalSlices: number
  sliceDurationS: number
}

// Reproduces the contract's §5 resolution formula for a pre-submit ticket preview only —
// never sent to the server, which independently computes and returns the authoritative
// values on AdvancedTwapState.
export function resolveClientSlicing(
  durationSeconds: number,
  requestedSlices: number
): ResolvedSlicing {
  const requested = requestedSlices > 0 ? requestedSlices : 10
  const totalSlices =
    durationSeconds / requested >= 10 ? requested : Math.max(1, Math.floor(durationSeconds / 10))
  const sliceDurationS = totalSlices > 0 ? durationSeconds / totalSlices : 0
  return { totalSlices, sliceDurationS }
}

export interface AdvancedTwapProgressDetails {
  progressLabel: string
  sliceLabel: string
  /** Null unless the run is currently paused (band breached). */
  pausedBandLabel: string | null
}

// Shared by the legacy dedicated-section row mapper (history-row.ts) and the Orders-table
// progress popover (order-columns-menu.tsx is unrelated — see
// advanced-twap-progress-popover.tsx) — a running/terminal AdvancedTwapState carries no
// single "price" or "value" the merged Orders row's Size/Price/Filled-Rem columns can show,
// so this is surfaced separately rather than lost.
export function resolveAdvancedTwapProgressDetails(
  advancedTwap: AdvancedTwapState
): AdvancedTwapProgressDetails {
  let progressFraction = '0'
  try {
    if (!new Decimal(advancedTwap.totalQuantity || '0').isZero()) {
      progressFraction = new Decimal(advancedTwap.executedQuantity || '0')
        .dividedBy(advancedTwap.totalQuantity)
        .toString()
    }
  } catch {
    progressFraction = '0'
  }

  const pausedBandLabel =
    advancedTwap.pausedAt && advancedTwap.upperBoundPct && advancedTwap.lowerBoundPct
      ? (() => {
          const bounds = resolveBandBounds(
            advancedTwap.arrivalPrice,
            advancedTwap.upperBoundPct!,
            advancedTwap.lowerBoundPct!,
            advancedTwap.currentSliceNumber
          )
          return `Allowed band: ${bounds.lower} - ${bounds.upper}`
        })()
      : null

  return {
    progressLabel: formatPercent(progressFraction),
    sliceLabel: sliceLabel(advancedTwap.currentSliceNumber, advancedTwap.totalSlices),
    pausedBandLabel,
  }
}
